import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCustomerList } from "../../APICalling/APICalling";
import {
	buildCustomerListRequest,
	customerMatchesSearch,
	serializeCustomerListExtraParams,
	sortCustomersByRelevance,
} from "../customerSearchUtils";
import logger from "../logger";

/**
 * Reusable hook around `getCustomerList` (the `/users/customer/list` endpoint).
 *
 * Encapsulates: debounced server-side search, in-memory response cache,
 * cursor-based pagination, and "stale request" guarding (ignores responses
 * whose query no longer matches the latest input). Mirrors the behaviour of
 * `CustomerSelector` so multiple dropdowns can share the same search flow.
 *
 * @param {object} options
 * @param {object} [options.extraParams]  Extra fields merged into the API payload
 *   (e.g. `{ membership_status: 'active' }`). Treated as static — pass a memoized
 *   object if it can change at runtime.
 * @param {number} [options.debounceMs=500]  Debounce delay applied to typing.
 * @param {boolean} [options.enabled=true]  When false, no requests are made
 *   (useful for closed dropdowns).
 * @param {(customer: object) => boolean} [options.filterFn]  Optional client-side
 *   filter applied on top of the search match (e.g. only members).
 */
const useCustomerSearch = ({
	extraParams = null,
	debounceMs = 500,
	enabled = true,
	filterFn = null,
} = {}) => {
	const [customers, setCustomers] = useState([]);
	const [filteredCustomers, setFilteredCustomers] = useState([]);
	const [searchQuery, setSearchQueryState] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [hasMore, setHasMore] = useState(true);

	const currentSearchRef = useRef("");
	const nextCursorRef = useRef(null);
	const cacheRef = useRef({});
	const debounceRef = useRef(null);
	const filteredCustomersRef = useRef([]);
	const isLoadingRef = useRef(false);
	const extraParamsRef = useRef(extraParams);
	const filterFnRef = useRef(filterFn);

	const extraParamsKey = useMemo(
		() => serializeCustomerListExtraParams(extraParams),
		[extraParams]
	);

	useEffect(() => {
		extraParamsRef.current = extraParams;
	}, [extraParams]);

	useEffect(() => {
		cacheRef.current = {};
	}, [extraParamsKey]);

	useEffect(() => {
		filterFnRef.current = filterFn;
	}, [filterFn]);

	useEffect(() => {
		filteredCustomersRef.current = filteredCustomers;
	}, [filteredCustomers]);

	useEffect(() => {
		isLoadingRef.current = isLoading;
	}, [isLoading]);

	useEffect(() => {
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, []);

	const applyFilters = useCallback((list, query) => {
		let next = Array.isArray(list) ? list : [];
		const ff = filterFnRef.current;
		if (typeof ff === "function") {
			next = next.filter((c) => c && ff(c));
		}
		const q = String(query || "").trim();
		if (q) {
			next = next.filter((c) => c && customerMatchesSearch(c, q));
			next = sortCustomersByRelevance(next, q);
		}
		return next;
	}, []);

	const fetchList = useCallback(
		async (searchText = "", cursor = null) => {
			if (!enabled) return;

			setIsLoading(true);
			setError(null);
			isLoadingRef.current = true;

			const requestExtraParams = extraParamsRef.current || {};
			const { item, searchForThisCall } = buildCustomerListRequest(
				searchText,
				cursor,
				requestExtraParams
			);
			const isFirstPage = !cursor;
			const cacheKey = `${searchForThisCall}_${cursor || "first"}_${serializeCustomerListExtraParams(requestExtraParams)}`;

			const updateLists = (newCustomers, pagination) => {
				const processed = applyFilters(newCustomers, searchForThisCall);

				setCustomers((prev) => {
					const combined = isFirstPage ? processed : [...prev, ...processed];
					return applyFilters(combined, searchForThisCall);
				});

				setFilteredCustomers((prev) => {
					const combined = isFirstPage ? processed : [...prev, ...processed];
					const finalList = applyFilters(combined, searchForThisCall);
					filteredCustomersRef.current = finalList;
					return finalList;
				});

				const hasMorePages =
					pagination?.hasMorePages ??
					(pagination?.currentPage != null && pagination?.lastPage != null
						? pagination.currentPage < pagination.lastPage
						: false);
				setHasMore(!!hasMorePages);
				nextCursorRef.current = pagination?.nextCursor ?? null;
			};

			if (cacheRef.current[cacheKey]) {
				const cached = cacheRef.current[cacheKey];
				const newCustomers = cached.clients || [];
				updateLists(newCustomers, cached.pagination);
				setIsLoading(false);
				isLoadingRef.current = false;
				return;
			}

			try {
				const response = await getCustomerList(item);
				const listPayload = response?.data?.data?.list || null;
				if (listPayload) {
					cacheRef.current[cacheKey] = listPayload;
				}
				const newCustomers = listPayload?.clients || [];

				// Guard against stale responses: drop if the user has moved on.
				if (currentSearchRef.current.trim() !== searchForThisCall) {
					return;
				}

				updateLists(newCustomers, listPayload?.pagination);
			} catch (err) {
				logger.error("Error fetching customer list:", err);
				if (currentSearchRef.current.trim() === searchForThisCall) {
					setError(
						"Network error. Please check your internet connection and try again."
					);
				}
			} finally {
				setIsLoading(false);
				isLoadingRef.current = false;
			}
		},
		[applyFilters, enabled]
	);

	const setSearchQuery = useCallback(
		(query) => {
			const nextQuery = query ?? "";
			setSearchQueryState(nextQuery);
			currentSearchRef.current = nextQuery;

			if (debounceRef.current) clearTimeout(debounceRef.current);

			debounceRef.current = setTimeout(() => {
				setCustomers([]);
				setFilteredCustomers([]);
				filteredCustomersRef.current = [];
				setHasMore(true);
				nextCursorRef.current = null;
				fetchList(nextQuery);
			}, debounceMs);
		},
		[debounceMs, fetchList]
	);

	const loadMore = useCallback(() => {
		if (!hasMore || isLoadingRef.current) return;
		fetchList(currentSearchRef.current, nextCursorRef.current);
	}, [fetchList, hasMore]);

	const reset = useCallback(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		setSearchQueryState("");
		currentSearchRef.current = "";
		setCustomers([]);
		setFilteredCustomers([]);
		filteredCustomersRef.current = [];
		setHasMore(true);
		nextCursorRef.current = null;
		setError(null);
	}, []);

	const clearCache = useCallback(() => {
		cacheRef.current = {};
	}, []);

	return {
		customers,
		filteredCustomers,
		searchQuery,
		setSearchQuery,
		isLoading,
		error,
		hasMore,
		fetchList,
		loadMore,
		reset,
		clearCache,
		currentSearchRef,
		filteredCustomersRef,
		isLoadingRef,
		nextCursorRef,
	};
};

export default useCustomerSearch;
